import { z } from 'zod'

const TimestampSchema = z.iso.datetime({ offset: true })
const ReasonCodeSchema = z.string().regex(/^[a-z][a-z0-9_]*$/)
// A Python `Decimal` reaches the wire as a string; both spellings are accepted
// and normalized to a number so a caller never has to know which arrived.
const MoneySchema = z.union([z.number().nonnegative(), z.string().regex(/^\d+(?:\.\d+)?$/).transform(Number)])
const SignedMoneySchema = z.union([z.number(), z.string().regex(/^-?\d+(?:\.\d+)?$/).transform(Number)])
const PercentSchema = z.union([z.number(), z.string().regex(/^-?\d+(?:\.\d+)?$/).transform(Number)])

export const YmDriftStateSchema = z.enum(['in_sync', 'price', 'discount_base', 'minimum_for_promos', 'missing_on_ym', 'never_collected', 'forge_price_not_set'])
export const YmDriftFieldNameSchema = z.enum(['price', 'discount_base', 'minimum_for_promos'])
export const YmBestsellerTierSchema = z.enum(['light', 'bestseller', 'super', 'top', 'none'])
export const YmBestsellerThresholdTierSchema = z.enum(['light', 'bestseller', 'super', 'top'])
export const YmPriceModeSchema = z.enum(['per_gram', 'price'])
export const YmDiscountBaseModeSchema = z.enum(['markup_percent', 'price', 'keep', 'remove'])
export const YmMinimumForPromosModeSchema = z.enum(['price', 'percent_of_price', 'keep', 'remove'])
export const YmReconcileDirectionSchema = z.enum(['forge_to_ym', 'ym_to_forge'])
export const YmPricingRefusalCodeSchema = z.enum([
  'weight_not_measured',
  'cost_not_known',
])

export const YmCodedMessageSchema = z.object({
  code: ReasonCodeSchema,
  message: z.string().min(1),
}).strict()

export const YmMarketPricesSchema = z.object({
  price: MoneySchema,
  discountBase: MoneySchema.nullable(),
  minimumForPromos: MoneySchema.nullable(),
  showcasePrice: MoneySchema.nullable(),
  showcaseNoPayPrice: MoneySchema.nullable(),
  showcaseCollectedAt: TimestampSchema.nullable(),
  showcaseUnavailableReason: ReasonCodeSchema.nullable(),
  currency: z.string().min(1),
}).strict().refine(
  (ym) => (ym.showcasePrice === null) !== (ym.showcaseUnavailableReason === null),
  { path: ['showcasePrice'], message: 'showcasePrice and showcaseUnavailableReason are exclusive' },
)

export const YmPromoSchema = z.object({
  promoId: z.string().min(1),
  name: z.string().min(1),
  participating: z.boolean(),
  sellerDiscountPercent: PercentSchema.nullable(),
  promoPrice: MoneySchema.nullable(),
  endsAt: TimestampSchema.nullable(),
  offerStatus: z.string().min(1).nullable().optional().default(null),
  startsAt: z.iso.datetime({ offset: true }).nullable().optional().default(null),
}).strict()

export const YmBestsellerThresholdSchema = z.object({
  tier: YmBestsellerThresholdTierSchema,
  maxPrice: MoneySchema,
}).strict()

export const YmBestsellerSchema = z.object({
  tier: YmBestsellerTierSchema,
  thresholds: z.array(YmBestsellerThresholdSchema),
}).strict()

export const YmCostsSchema = z.object({
  commission: MoneySchema,
}).strict()

export const YmQuarantineSchema = z.object({
  inQuarantine: z.boolean(),
  verdictPrice: MoneySchema.nullable(),
}).strict()

// No Forge intent exists until the first apply or ym_to_forge reconcile, so every Forge price may be null.
export const YmForgePricesSchema = z.object({
  weightGrams: z.number().positive().nullable(),
  weightBasis: z.string().min(1).optional().default('общий вес'),
  purchaseCost: MoneySchema.nullable().optional().default(null),
  purchaseCostUnavailableReason: ReasonCodeSchema.nullable().optional().default(null),
  purchaseCostSourceDocument: z.string().min(1).nullable().optional().default(null),
  purchaseCostSyncedAt: TimestampSchema.nullable().optional().default(null),
  purchaseCostSourceAdapter: z.string().min(1).nullable().optional().default(null),
  price: MoneySchema.nullable(),
  discountBase: MoneySchema.nullable(),
  minimumForPromos: MoneySchema.nullable(),
  pricePerGram: MoneySchema.nullable(),
}).strict().refine(
  (forge) => (forge.weightGrams !== null && forge.price !== null) || forge.pricePerGram === null,
  { path: ['pricePerGram'], message: 'pricePerGram needs both a price and a measured weight' },
)

export const YmDriftFieldSchema = z.object({
  field: YmDriftFieldNameSchema,
  forge: MoneySchema.nullable(),
  ym: MoneySchema.nullable(),
}).strict()

export const YmDriftSchema = z.object({
  state: YmDriftStateSchema,
  fields: z.array(YmDriftFieldSchema),
}).strict()

export const YmRecommendationSchema = z.object({
  price: MoneySchema,
  discountBase: MoneySchema.nullable(),
  minimumForPromos: MoneySchema.nullable(),
  costFloorPrice: MoneySchema.nullable().optional().default(null),
  costTargetPrice: MoneySchema.nullable().optional().default(null),
  perGramFloorPrice: MoneySchema.nullable().optional().default(null),
  perGramTargetPrice: MoneySchema.nullable().optional().default(null),
  bindingRule: z.enum(['cost', 'per_gram']).nullable().optional().default(null),
  reasons: z.array(YmCodedMessageSchema),
}).strict()

export const YmRecommendationRefusalSchema = z.object({
  code: YmPricingRefusalCodeSchema,
  message: z.string().min(1),
}).strict()

export const YmPricingEarningsSchema = z.object({
  pricePerGram: MoneySchema.nullable(),
  payoutAfterMarketFee: SignedMoneySchema.nullable(),
  earnings: SignedMoneySchema.nullable(),
  earningsPercent: PercentSchema.nullable(),
  unavailableReason: ReasonCodeSchema.nullable(),
}).strict()

export const YmPricingPromoEarningsSchema = z.object({
  promoId: z.string().min(1),
  name: z.string().min(1),
  earnings: YmPricingEarningsSchema,
}).strict()

const YmOfferPricingShape = z.object({
  offerId: z.string().min(1),
  productId: z.string().uuid(),
  label: z.string().min(1),
  collectedAt: TimestampSchema.nullable(),
  stale: z.boolean(),
  ym: YmMarketPricesSchema.nullable(),
  promos: z.array(YmPromoSchema),
  bestseller: YmBestsellerSchema.nullable(),
  bestsellerUnavailableReason: ReasonCodeSchema.nullable(),
  competitorLowestPrice: MoneySchema.nullable(),
  competitorUnavailableReason: ReasonCodeSchema.nullable(),
  costs: YmCostsSchema.nullable(),
  costsUnavailableReason: ReasonCodeSchema.nullable(),
  quarantine: YmQuarantineSchema,
  forge: YmForgePricesSchema,
  drift: YmDriftSchema,
  recommendation: YmRecommendationSchema.nullable(),
  recommendationRefusal: YmRecommendationRefusalSchema.nullable().default(null),
  currentEarnings: YmPricingEarningsSchema.nullable().optional().default(null),
  promoEarnings: z.array(YmPricingPromoEarningsSchema).optional().default([]),
  recommendationEarnings: YmPricingEarningsSchema.nullable().optional().default(null),
}).strict()

type YmOfferPricingShape = z.infer<typeof YmOfferPricingShape>

/** Every absent value names why it is absent, so the page never shows a blank or a zero in its place. */
function refineOfferPricing<Offer extends YmOfferPricingShape>(offer: Offer, context: z.core.$RefinementCtx<Offer>): void {
  const exclusive: readonly [keyof YmOfferPricingShape, boolean, boolean][] = [
    ['bestseller', offer.bestseller === null, offer.bestsellerUnavailableReason === null],
    ['competitorLowestPrice', offer.competitorLowestPrice === null, offer.competitorUnavailableReason === null],
    ['costs', offer.costs === null, offer.costsUnavailableReason === null],
  ]
  for (const [path, valueAbsent, reasonAbsent] of exclusive) {
    if (valueAbsent === reasonAbsent) {
      context.addIssue({ code: 'custom', path: [path], message: `${path} and its unavailable reason are exclusive` })
    }
  }
  if (offer.drift.state === 'never_collected' && (offer.collectedAt !== null || offer.ym !== null)) {
    context.addIssue({ code: 'custom', path: ['drift', 'state'], message: 'never_collected offers carry no collected values' })
  }
  if (offer.drift.state === 'missing_on_ym' && offer.ym !== null) {
    context.addIssue({ code: 'custom', path: ['ym'], message: 'missing_on_ym offers carry no market prices' })
  }
  if (offer.ym === null && offer.drift.state !== 'never_collected' && offer.drift.state !== 'missing_on_ym') {
    context.addIssue({ code: 'custom', path: ['ym'], message: 'market prices are absent only for never_collected or missing_on_ym' })
  }
}

export const YmOfferPricingSchema = YmOfferPricingShape.superRefine(refineOfferPricing)

export const YmPriceSnapshotSchema = z.object({
  collectedAt: TimestampSchema,
  price: MoneySchema.nullable(),
  discountBase: MoneySchema.nullable(),
  minimumForPromos: MoneySchema.nullable(),
  showcasePrice: MoneySchema.nullable(),
  showcaseNoPayPrice: MoneySchema.nullable(),
  showcaseCollectedAt: TimestampSchema.nullable(),
  showcaseUnavailableReason: ReasonCodeSchema.nullable(),
  promos: z.array(YmPromoSchema),
}).strict()

export const YmOfferPricingDetailSchema = YmOfferPricingShape.extend({
  history: z.array(YmPriceSnapshotSchema).max(30),
}).strict().superRefine(refineOfferPricing)

export const YmOfferPricingPageSchema = z.object({
  items: z.array(YmOfferPricingSchema),
  nextCursor: z.string().nullable(),
}).strict()

function keepOrValue<Mode extends string>(mode: z.ZodType<Mode>) {
  return z.object({
    mode,
    value: z.number().nonnegative().nullable(),
  }).strict().refine(
    (setting) => (setting.mode === 'keep') === (setting.value === null),
    { path: ['value'], message: 'value is null exactly when mode is keep' },
  )
}

export const YmPriceChangeSchema = z.object({
  offerId: z.string().min(1),
  mode: YmPriceModeSchema,
  value: z.number().positive(),
  discountBase: keepOrValue(YmDiscountBaseModeSchema),
  minimumForPromos: keepOrValue(YmMinimumForPromosModeSchema),
  promoParticipation: z.array(z.object({
    promoId: z.string().min(1),
    participate: z.boolean(),
  }).strict()),
  bandMode: z.boolean().optional().default(false),
}).strict()

export const YmReconcileSchema = z.object({
  offerIds: z.array(z.string().min(1)).min(1),
  direction: YmReconcileDirectionSchema,
}).strict()

export const YmPricingPreviewRequestSchema = z.union([
  z.object({ changes: z.array(YmPriceChangeSchema).min(1) }).strict(),
  z.object({ reconcile: YmReconcileSchema }).strict(),
])

export const YmPricePointSchema = z.object({
  price: MoneySchema.nullable(),
  discountBase: MoneySchema.nullable(),
  minimumForPromos: MoneySchema.nullable(),
  promoParticipation: z.array(z.object({
    promoId: z.string().min(1),
    name: z.string().min(1),
    participating: z.boolean(),
  }).strict()),
}).strict()

export const YmPricingPreviewRowSchema = z.object({
  offerId: z.string().min(1),
  before: YmPricePointSchema,
  after: YmPricePointSchema.nullable(),
  pricePerGram: MoneySchema.nullable(),
  changePercent: PercentSchema.nullable(),
  quarantineRisk: z.boolean(),
  warnings: z.array(YmCodedMessageSchema),
  refusals: z.array(YmCodedMessageSchema),
}).strict().refine(
  (row) => row.after !== null || row.refusals.length > 0,
  { path: ['after'], message: 'a row without an outcome must name its refusal' },
)

export const YmPricingPreviewSchema = z.object({
  previewHash: z.string().min(1),
  expiresAt: TimestampSchema,
  rows: z.array(YmPricingPreviewRowSchema),
}).strict()

export const YmPricingApplyRequestSchema = z.object({
  previewHash: z.string().min(1),
  overrides: z.record(z.string().uuid(), z.number().int().positive()).optional(),
}).strict()

export const YmQuarantineConfirmRequestSchema = z.object({
  offerIds: z.array(z.string().min(1)).min(1),
  previewHash: z.string().min(1),
}).strict()

const YmEconomicsSummarySchema = z.object({
  average: MoneySchema,
  median: MoneySchema,
  min: MoneySchema,
  max: MoneySchema,
}).strict()

// Mirrors YmEconomicsStatsView in forge/ymarket_pricing/router.py.
export const YmEconomicsStatsSchema = z.object({
  selectedCount: z.number().int().nonnegative(),
  count: z.number().int().nonnegative(),
  price: YmEconomicsSummarySchema.nullable(),
  pricePerGram: YmEconomicsSummarySchema.nullable(),
  averageEarningsPercent: z.number().finite().nullable(),
  quartiles: z.object({ q1: MoneySchema.nullable(), q3: MoneySchema.nullable(), iqr: MoneySchema.nullable() }).strict(),
  outlierIds: z.array(z.string().uuid()),
  outlierCount: z.number().int().nonnegative(),
}).strict()

// Mirrors EconomicsFilter in forge/ymarket_pricing/economics.py.
export const YmEconomicsFilterSchema = z.enum([
  'below_floor',
  'in_promo',
  'boosted',
  'no_cost',
  'stale',
  'outliers',
  'drift',
  'forge_price_not_set',
])

// Mirrors StepSource in forge/ymarket_pricing/economics.py.
export const YmEconomicsStepSourceSchema = z.enum([
  'ym_offer_prices',
  'ym_promos',
  'ym_orders',
  'ym_tariffs',
  'ym_bids',
  'earnings_formula',
  'onec_cost',
  'ym_prices_report',
])

export const YmEconomicsDriverSchema = z.object({
  kind: z.enum(['promo', 'boost']),
  name: z.string().min(1),
  discountPercent: z.number().finite().nullable(),
  bid: z.number().finite().nullable(),
  promoPrice: MoneySchema.nullable(),
  startsAt: TimestampSchema.nullable(),
  endsAt: TimestampSchema.nullable(),
  active: z.boolean(),
  endingSoon: z.boolean(),
}).strict()

export const YmEconomicsStepSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  value: z.number().finite().nullable(),
  source: YmEconomicsStepSourceSchema,
  sourceDetail: z.string().nullable(),
  updatedAt: TimestampSchema.nullable(),
  unavailableReason: ReasonCodeSchema.nullable(),
}).strict()

// Mirrors YmEconomicsPricesView in forge/ymarket_pricing/views.py: each price keeps its side.
export const YmEconomicsPricesSchema = z.object({
  forgePrice: MoneySchema.nullable(),
  forgeDiscountBase: MoneySchema.nullable(),
  forgeMinimumForPromos: MoneySchema.nullable(),
  ymPrice: MoneySchema.nullable(),
  ymDiscountBase: MoneySchema.nullable(),
  ymMinimumForPromos: MoneySchema.nullable(),
  showcasePrice: MoneySchema.nullable().optional().default(null),
  driftState: YmDriftStateSchema,
  collectedAt: TimestampSchema.nullable(),
}).strict()

// Mirrors YmShowcaseCorrectionEntryView: one setting of the showcase correction, with who set it and when.
export const YmShowcaseCorrectionEntrySchema = z.object({
  kind: z.enum(['residual', 'pay_discount']),
  category: z.string().nullable(),
  value: z.number().nonnegative(),
  source: z.enum(['manual', 'cabinet_export', 'orders_estimate']),
  actor: z.string(),
  note: z.string().nullable(),
  sampleCount: z.number().int().positive().nullable(),
  observedAt: TimestampSchema.nullable(),
  setAt: TimestampSchema,
}).strict()

// Mirrors FormulaVariableView: one name a formula may use.
export const FormulaVariableSchema = z.object({
  name: z.string(),
  type: z.enum(['number', 'text', 'bool']),
  label: z.string(),
  description: z.string(),
  values: z.array(z.string()).nullable(),
}).strict()

export const FormulaFunctionSchema = z.object({
  name: z.string(),
  signature: z.string(),
  description: z.string(),
}).strict()

// Mirrors FormulaVersionView: one saved source of one formula.
export const FormulaVersionSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  source: z.string(),
  actor: z.string(),
  note: z.string().nullable(),
  changeId: z.string().uuid(),
  setAt: TimestampSchema,
}).strict()

// Mirrors FormulaDefinitionView: one editable formula and the source in force.
export const FormulaDefinitionSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  group: z.string(),
  default: z.string(),
  source: z.string(),
  builtIn: z.boolean(),
  version: FormulaVersionSchema.nullable(),
  references: z.array(z.string()),
  variables: z.array(FormulaVariableSchema),
  storedError: z.string().nullable(),
  // The value per metal when the source in force names nothing but the metal.
  byMetal: z.array(z.object({ metal: z.string(), value: z.number() }).strict()).nullable(),
}).strict()

// Mirrors YmShowcaseFormulaView.
export const YmShowcaseFormulaSchema = z.object({
  formulas: z.array(FormulaDefinitionSchema),
  functions: z.array(FormulaFunctionSchema),
  limits: z.object({ maxLength: z.number().int(), maxDepth: z.number().int(), maxNodes: z.number().int() }).strict(),
  history: z.array(FormulaVersionSchema),
  proposal: YmShowcaseCorrectionEntrySchema.nullable(),
  oracleOfferIds: z.array(z.string()),
}).strict()

export const YmShowcaseFormulaBodySchema = z.object({
  formulas: z.record(z.string(), z.string().max(2000)),
  note: z.string().max(500).optional(),
}).strict()

export const YmShowcaseFormulaPreviewBodySchema = z.object({
  formulas: z.record(z.string(), z.string().max(2000)),
  offerIds: z.array(z.string().min(1)).max(20),
}).strict()

export const FormulaValueSchema = z.object({
  value: z.number().nullable(),
  reason: ReasonCodeSchema.nullable(),
  message: z.string().nullable(),
}).strict()

export const FormulaResultsSchema = z.object({
  showcase: FormulaValueSchema,
  showcasePay: FormulaValueSchema,
  inHand: FormulaValueSchema,
  inHandPerGram: z.number().nullable(),
}).strict()

export const YmShowcaseFormulaPreviewRowSchema = z.object({
  offerId: z.string(),
  label: z.string().nullable(),
  oracle: z.boolean(),
  cabinetNoPay: MoneySchema.nullable(),
  cabinetPay: MoneySchema.nullable(),
  onDisplay: MoneySchema.nullable(),
  onDisplayPay: MoneySchema.nullable(),
  ourPrice: MoneySchema.nullable(),
  category: z.string().nullable(),
  weight: z.number().nullable(),
  unavailableReason: ReasonCodeSchema.nullable(),
  current: FormulaResultsSchema.nullable(),
  candidate: FormulaResultsSchema.nullable(),
}).strict()

export const YmShowcaseFormulaPreviewSchema = z.object({
  rows: z.array(YmShowcaseFormulaPreviewRowSchema),
}).strict()

// A refused formula: which key, the 1-based character and the Russian sentence.
export const FormulaRefusalSchema = z.object({
  detail: z.object({
    code: z.string(),
    message: z.string(),
    key: z.string().nullable(),
    position: z.number().int().nullable(),
  }),
})

// Mirrors YmEconomicsShowcaseView: «Цена на витрине» with and without Yandex Pay from the owner's formulas over the report.
export const YmEconomicsShowcaseSchema = z.object({
  payPrice: MoneySchema.nullable(),
  noPayPrice: MoneySchema.nullable(),
  collectedAt: TimestampSchema.nullable(),
  unavailableReason: ReasonCodeSchema.nullable(),
  payUnavailableReason: ReasonCodeSchema.nullable().optional().default(null),
  reportPrice: MoneySchema.nullable().optional().default(null),
  reportPayPrice: MoneySchema.nullable().optional().default(null),
  formulaSetAt: TimestampSchema.nullable().optional().default(null),
  // The piece's cheapThreshold, ₽/г, or why it has none.
  cheapThreshold: MoneySchema.nullable().optional().default(null),
  cheapThresholdReason: ReasonCodeSchema.nullable().optional().default(null),
}).strict()

// Mirrors YmPriceAttractivenessView: our Market price against the «Цены» report's green and red bounds.
export const YmPriceAttractivenessLevelSchema = z.enum(['optimal', 'average', 'low'])
export const YmPriceAttractivenessSchema = z.object({
  level: YmPriceAttractivenessLevelSchema,
  price: MoneySchema,
  greenThreshold: MoneySchema,
  redThreshold: MoneySchema,
  collectedAt: TimestampSchema,
}).strict()

// Mirrors YmEconomicsPromoView and YmEconomicsBoostView: the «Модификаторы Маркета» of one offer.
export const YmEconomicsPromoSchema = z.object({
  promoId: z.string(),
  name: z.string(),
  participating: z.boolean(),
  offerStatus: z.string().nullable(),
  promoPrice: MoneySchema.nullable(),
  startsAt: TimestampSchema.nullable(),
  endsAt: TimestampSchema.nullable(),
}).strict()

export const YmEconomicsBoostSchema = z.object({
  bidPercent: z.number().nonnegative().nullable(),
  unavailableReason: ReasonCodeSchema.nullable(),
  campaigns: z.array(z.string()),
  campaignShows: z.number().int().nonnegative().nullable(),
  reportOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
}).strict()

// Mirrors YmEconomicsSyncView: the newest order's buyer price against the showcase at that moment.
export const YmEconomicsSyncSchema = z.object({
  orderId: z.string(),
  orderedAt: TimestampSchema,
  buyerPrice: MoneySchema,
  showcasePrice: MoneySchema.nullable(),
  deviationPercent: z.number().finite().nullable(),
  drifted: z.boolean(),
  unavailableReason: z.enum(['showcase_not_collected_at_order', 'showcase_not_collected']).nullable(),
}).strict()

export const YmEconomicsPieceSchema = z.object({
  productId: z.string().uuid(),
  offerId: z.string(),
  label: z.string(),
  sellsFor: MoneySchema.nullable(),
  sellsForReason: ReasonCodeSchema.nullable(),
  weightGrams: MoneySchema.nullable(),
  pricePerGram: MoneySchema.nullable(),
  purchaseCost: MoneySchema.nullable(),
  earnings: YmPricingEarningsSchema,
  netPriceBasis: z.enum(['seller_price', 'seller_promo_price', 'showcase_pay_price']).optional().default('seller_price'),
  deliveryToCustomer: MoneySchema.nullable().optional().default(null),
  deliveryUnavailableReason: ReasonCodeSchema.nullable().optional().default(null),
  net: MoneySchema.nullable().optional().default(null),
  netUnavailableReason: ReasonCodeSchema.nullable().optional().default(null),
  netPerGram: MoneySchema.nullable().optional().default(null),
  netPerGramUnavailableReason: ReasonCodeSchema.nullable().optional().default(null),
  commissionRate: PercentSchema.nullable().optional().default(null),
  targetNetPerGram: MoneySchema.nullable().optional().default(null),
  targetExcludedReason: ReasonCodeSchema.nullable().optional().default(null),
  priceForTarget: MoneySchema.nullable().optional().default(null),
  priceForTargetUnavailableReason: ReasonCodeSchema.nullable().optional().default(null),
  belowFloor: z.boolean().nullable(),
  stale: z.boolean(),
  outlier: z.boolean().nullable(),
  drivers: z.array(YmEconomicsDriverSchema),
  waterfall: z.array(YmEconomicsStepSchema),
  prices: YmEconomicsPricesSchema.nullable().optional().default(null),
  showcase: YmEconomicsShowcaseSchema.nullable().optional().default(null),
  attractiveness: YmPriceAttractivenessSchema.nullable().optional().default(null),
  promos: z.array(YmEconomicsPromoSchema).optional().default([]),
  boost: YmEconomicsBoostSchema.nullable().optional().default(null),
  snapshotCollectedAt: TimestampSchema.nullable().optional().default(null),
  syncCheck: YmEconomicsSyncSchema.nullable().optional().default(null),
}).strict()

export const YmEconomicsPageSchema = z.object({
  items: z.array(YmEconomicsPieceSchema),
  nextCursor: z.string().nullable(),
}).strict()

export const YmEconomicsSelectionFilterSchema = z.record(z.string(), z.unknown())

export const YmEconomicsStatsRequestSchema = z.union([
  z.object({ ids: z.array(z.string().uuid()).min(1) }).strict(),
  z.object({ filter: YmEconomicsSelectionFilterSchema, expectedTotal: z.number().int().nonnegative().optional() }).strict(),
])

export const YmEconomicsBulkModeSchema = z.enum(['percent', 'price', 'per_gram', 'policy', 'net_per_gram', 'auto'])
export const YmEconomicsBulkChannelSchema = z.enum(['ymarket', 'wildberries'])

export const YmEconomicsBulkPreviewRequestSchema = z.object({
  productIds: z.array(z.string().uuid()).min(1).max(500).optional(),
  filter: YmEconomicsSelectionFilterSchema.optional(),
  expectedTotal: z.number().int().nonnegative().optional(),
  rollbackOperationId: z.string().uuid().optional(),
  mode: YmEconomicsBulkModeSchema,
  value: z.number().finite().optional(),
  channels: z.array(YmEconomicsBulkChannelSchema).min(1).max(2),
  ymarketStrikeMarkupPercent: z.number().int().min(0).max(1000).nullable(),
  overrides: z.record(z.string().uuid(), z.number().int().positive()).optional(),
}).strict().refine(
  (request) => request.rollbackOperationId === undefined
    ? (request.productIds === undefined) !== (request.filter === undefined)
    : request.productIds === undefined
      && request.filter === undefined
      && request.expectedTotal === undefined
      && request.overrides === undefined
      && request.mode === 'auto'
      && request.value === undefined
      && request.channels.length === 1
      && request.channels[0] === 'ymarket',
  { message: 'choose a selection or a guarded auto rollback, not both' },
).refine(
  (request) => request.mode !== 'auto' || (request.value === undefined && request.channels.length === 1 && request.channels[0] === 'ymarket'),
  { message: 'auto requires no value and the ymarket channel only' },
).refine(
  (request) => request.overrides === undefined || request.mode === 'auto',
  { message: 'overrides require auto mode' },
).refine(
  (request) => request.productIds === undefined || request.overrides === undefined
    || Object.keys(request.overrides).every((productId) => request.productIds?.includes(productId)),
  { message: 'overrides must belong to the selected product ids' },
).refine(
  (request) => request.filter === undefined ? request.expectedTotal === undefined : request.expectedTotal !== undefined,
  { message: 'filter requires expectedTotal, which is only valid with filter' },
)

export const YmEconomicsBulkShowcaseEstimateSchema = z.object({
  basis: z.enum(['own', 'similar']),
  ratio: z.number().positive(),
  groupLevel: z.enum(['metal_type_band', 'metal_type', 'metal', 'all']),
  groupCount: z.number().int().positive(),
  groupMedian: z.number().positive(),
  buyerPrice: MoneySchema.nullable(),
  reportCollectedAt: TimestampSchema,
}).strict()

export const YmEconomicsBulkAutoInputsSchema = z.object({
  onecCost: z.number().finite().nullable(),
  onecCostDate: z.string().date().nullable(),
  cbRate: z.number().finite().nullable(),
  cbDate: z.string().date().nullable(),
  feeRate: z.number().finite().nullable(),
  feeTariffSnapshotAt: TimestampSchema.nullable(),
  compositionConventionVersion: z.string().min(1),
}).strict()

export const YmEconomicsBulkAutoPromoSchema = z.object({
  promoId: z.string().min(1),
  name: z.string().min(1),
  participating: z.boolean(),
  sellerDiscountPercent: PercentSchema.nullable(),
  sellerPromoPrice: MoneySchema.nullable(),
  marketDiscountPercent: PercentSchema.nullable(),
  marketDiscountSource: z.enum(['витрина', 'формула d_ref']).nullable(),
  buyerPrice: MoneySchema.nullable(),
  settlement: MoneySchema.nullable(),
  delivery: MoneySchema.nullable(),
  tax: MoneySchema.nullable(),
  netAfterDeliveryTax: SignedMoneySchema.nullable(),
  marginFloorAmount: MoneySchema.nullable(),
  keepsMarginFloor: z.boolean().nullable(),
  unavailableReason: z.string().min(1).nullable(),
}).strict().refine(
  (promo) => (promo.keepsMarginFloor === null) === (promo.unavailableReason !== null),
  { path: ['unavailableReason'], message: 'a missing margin result carries exactly one unavailable reason' },
)

export const YmEconomicsBulkAutoSchema = z.object({
  stoneAllowance: MoneySchema.nullable(),
  stoneReserve: MoneySchema.nullable(),
  minimumForPromos: z.number().int().nullable(),
  markupVsCgPercent: MoneySchema.nullable(),
  markupVsOnecPercent: MoneySchema.nullable(),
  bindingReason: z.enum(['по граммам', 'по себестоимости', 'по наценке']).nullable(),
  showcasePay: MoneySchema.nullable(),
  showcaseNonPay: MoneySchema.nullable(),
  projectedProfit: MoneySchema.nullable(),
  changePercent: z.number().finite().nullable(),
  requiresExtraConfirmation: z.boolean(),
  confirmationThresholdPercent: z.number().finite().nonnegative().nullable(),
  costFloor: z.number().int().positive().nullable(),
  costBoundary: MoneySchema.nullable(),
  crossedFactor: z.number().positive().nullable(),
  promoFactor: z.number().positive().nullable(),
  estimateOnly: z.boolean(),
  staleFacts: z.boolean(),
  bestsellerTier: YmBestsellerTierSchema.nullable(),
  bestsellerThresholds: z.array(YmBestsellerThresholdSchema),
  bestsellerUnavailableReason: z.string().min(1).nullable(),
  promos: z.array(YmEconomicsBulkAutoPromoSchema),
  inputs: YmEconomicsBulkAutoInputsSchema,
}).strict().superRefine((auto, context) => {
  const hasThresholds = auto.bestsellerThresholds.length > 0
  const hasUnavailableReason = auto.bestsellerUnavailableReason !== null
  if (hasThresholds === hasUnavailableReason) {
    context.addIssue({
      code: 'custom',
      path: ['bestsellerUnavailableReason'],
      message: 'bestseller thresholds and their unavailable reason are exclusive',
    })
  }
})

export const YmEconomicsBulkRowSchema = z.object({
  productId: z.string().uuid(),
  channel: YmEconomicsBulkChannelSchema,
  label: z.string().min(1),
  offerId: z.string().nullable(),
  currentPrice: z.number().int().nullable(),
  currentOldPrice: z.number().int().nullable(),
  currentMinimumForPromos: z.number().int().nullable().optional().default(null),
  newPrice: z.number().int().nullable(),
  newOldPrice: z.number().int().nullable(),
  newMinimumForPromos: z.number().int().nullable().optional().default(null),
  difference: z.number().int().nullable(),
  beforePricePerGram: MoneySchema.nullable(),
  afterPricePerGram: MoneySchema.nullable(),
  weightGrams: MoneySchema.nullable().optional().default(null),
  beforeNetPerGram: MoneySchema.nullable().optional().default(null),
  afterNetPerGram: MoneySchema.nullable().optional().default(null),
  netPriceBasis: z.enum(['seller_price', 'seller_promo_price', 'showcase_pay_price']).nullable().optional().default(null),
  showcaseEstimate: YmEconomicsBulkShowcaseEstimateSchema.nullable().optional().default(null),
  beforeEarnings: YmPricingEarningsSchema,
  afterEarnings: YmPricingEarningsSchema,
  refusals: z.array(YmCodedMessageSchema),
  warnings: z.array(YmCodedMessageSchema).optional().default([]),
  quarantineRisk: z.boolean(),
  notInStock: z.boolean(),
  auto: YmEconomicsBulkAutoSchema.nullable().optional().default(null),
}).strict()

export const YmEconomicsBulkPreviewSchema = z.object({
  previewHash: z.string().min(1),
  expiresAt: TimestampSchema,
  mode: YmEconomicsBulkModeSchema,
  writer: z.enum(['price_batch', 'ymarket_pricing']),
  selectedCount: z.number().int().nonnegative(),
  rows: z.array(YmEconomicsBulkRowSchema),
  changedCount: z.number().int().nonnegative(),
  refusedCount: z.number().int().nonnegative(),
  canApply: z.boolean().optional().default(true),
  estimateOnly: z.boolean().optional().default(false),
  promoAutoEnrolWarning: z.string().nullable().optional().default(null),
  refreshOperationId: z.string().uuid().nullable().optional().default(null),
  refreshMessage: z.string().nullable().optional().default(null),
}).strict()

export type YmDriftState = z.infer<typeof YmDriftStateSchema>
export type YmDriftFieldName = z.infer<typeof YmDriftFieldNameSchema>
export type YmBestsellerTier = z.infer<typeof YmBestsellerTierSchema>
export type YmPriceMode = z.infer<typeof YmPriceModeSchema>
export type YmDiscountBaseMode = z.infer<typeof YmDiscountBaseModeSchema>
export type YmMinimumForPromosMode = z.infer<typeof YmMinimumForPromosModeSchema>
export type YmReconcileDirection = z.infer<typeof YmReconcileDirectionSchema>
export type YmPricingRefusalCode = z.infer<typeof YmPricingRefusalCodeSchema>
export type YmPricingEarnings = z.infer<typeof YmPricingEarningsSchema>
export type YmPricingPromoEarnings = z.infer<typeof YmPricingPromoEarningsSchema>
export type YmCodedMessage = z.infer<typeof YmCodedMessageSchema>
export type YmPromo = z.infer<typeof YmPromoSchema>
export type YmOfferPricing = z.infer<typeof YmOfferPricingSchema>
export type YmPriceSnapshot = z.infer<typeof YmPriceSnapshotSchema>
export type YmOfferPricingDetail = z.infer<typeof YmOfferPricingDetailSchema>
export type YmOfferPricingPage = z.infer<typeof YmOfferPricingPageSchema>
export type YmPriceChange = z.infer<typeof YmPriceChangeSchema>
export type YmPricingPreviewRequest = z.infer<typeof YmPricingPreviewRequestSchema>
export type YmPricePoint = z.infer<typeof YmPricePointSchema>
export type YmPricingPreviewRow = z.infer<typeof YmPricingPreviewRowSchema>
export type YmPricingPreview = z.infer<typeof YmPricingPreviewSchema>
export type YmQuarantineConfirmRequest = z.infer<typeof YmQuarantineConfirmRequestSchema>
export type YmEconomicsStats = z.infer<typeof YmEconomicsStatsSchema>
export type YmEconomicsFilter = z.infer<typeof YmEconomicsFilterSchema>
export type YmEconomicsStepSource = z.infer<typeof YmEconomicsStepSourceSchema>
export type YmEconomicsDriver = z.infer<typeof YmEconomicsDriverSchema>
export type YmEconomicsStep = z.infer<typeof YmEconomicsStepSchema>
export type YmEconomicsPrices = z.infer<typeof YmEconomicsPricesSchema>
export type YmEconomicsShowcase = z.infer<typeof YmEconomicsShowcaseSchema>
export type YmPriceAttractivenessLevel = z.infer<typeof YmPriceAttractivenessLevelSchema>
export type YmPriceAttractiveness = z.infer<typeof YmPriceAttractivenessSchema>
export type YmEconomicsPromo = z.infer<typeof YmEconomicsPromoSchema>
export type YmEconomicsBoost = z.infer<typeof YmEconomicsBoostSchema>
export type YmEconomicsSync = z.infer<typeof YmEconomicsSyncSchema>
export type YmShowcaseCorrectionEntry = z.infer<typeof YmShowcaseCorrectionEntrySchema>
export type FormulaVariable = z.infer<typeof FormulaVariableSchema>
export type FormulaFunction = z.infer<typeof FormulaFunctionSchema>
export type FormulaDefinition = z.infer<typeof FormulaDefinitionSchema>
export type FormulaVersion = z.infer<typeof FormulaVersionSchema>
export type FormulaValue = z.infer<typeof FormulaValueSchema>
export type FormulaResults = z.infer<typeof FormulaResultsSchema>
export type YmShowcaseFormula = z.infer<typeof YmShowcaseFormulaSchema>
export type YmShowcaseFormulaBody = z.infer<typeof YmShowcaseFormulaBodySchema>
export type YmShowcaseFormulaPreviewBody = z.infer<typeof YmShowcaseFormulaPreviewBodySchema>
export type YmShowcaseFormulaPreviewRow = z.infer<typeof YmShowcaseFormulaPreviewRowSchema>
export type YmShowcaseFormulaPreview = z.infer<typeof YmShowcaseFormulaPreviewSchema>
export type YmEconomicsPiece = z.infer<typeof YmEconomicsPieceSchema>
export type YmEconomicsPage = z.infer<typeof YmEconomicsPageSchema>
export type YmEconomicsStatsRequest = z.infer<typeof YmEconomicsStatsRequestSchema>
export type YmEconomicsBulkMode = z.infer<typeof YmEconomicsBulkModeSchema>
export type YmEconomicsBulkChannel = z.infer<typeof YmEconomicsBulkChannelSchema>
export type YmEconomicsBulkPreviewRequest = z.infer<typeof YmEconomicsBulkPreviewRequestSchema>
export type YmEconomicsBulkAutoPromo = z.infer<typeof YmEconomicsBulkAutoPromoSchema>
export type YmEconomicsBulkAuto = z.infer<typeof YmEconomicsBulkAutoSchema>
export type YmEconomicsBulkRow = z.infer<typeof YmEconomicsBulkRowSchema>
export type YmEconomicsBulkShowcaseEstimate = z.infer<typeof YmEconomicsBulkShowcaseEstimateSchema>
export type YmEconomicsBulkPreview = z.infer<typeof YmEconomicsBulkPreviewSchema>
